import { useState } from "react";
import { Copy, Share2, RefreshCw, EyeOff, Eye } from "lucide-react";

export default function TemporaryPassword() {
  const [tempPassword, setTempPassword] = useState("");
  const [expireTime, setExpireTime] = useState(5);
  const [link, setLink] = useState("");
  const [copied, setCopied] = useState(false);
  const [shared, setShared] = useState(false);

  // Custom password options
  const [customPassword, setCustomPassword] = useState(""); // user input
  const [length, setLength] = useState(12);
  const [useUpper, setUseUpper] = useState(true);
  const [useLower, setUseLower] = useState(true);
  const [useNumbers, setUseNumbers] = useState(true);
  const [useSymbols, setUseSymbols] = useState(true);

  const [showPassword, setShowPassword] = useState(false); // hidden by default


  const generateTemporaryPassword = () => {
    let pass = "";

    // Use custom password if user entered
    if (customPassword) {
      pass = customPassword;
    } else {
      let chars = "";
      if (useUpper) chars += "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
      if (useLower) chars += "abcdefghijklmnopqrstuvwxyz";
      if (useNumbers) chars += "0123456789";
      if (useSymbols) chars += "!@#$%^&*()_+{}[]";

      if (!chars) return alert("Select at least one character type!");

      for (let i = 0; i < length; i++) {
        pass += chars[Math.floor(Math.random() * chars.length)];
      }
    }

    setTempPassword(pass);

    // Generate hash ID link
    const id = Math.random().toString(36).substr(2, 8);
    const tempLink = `${window.location.origin}/temp/${id}`;
    setLink(tempLink);

    // Save password + expiry
    const expiry = Date.now() + expireTime * 60 * 1000;
    const stored = JSON.parse(localStorage.getItem("tempPasswords") || "{}");
    stored[id] = { password: pass, expiry };
    localStorage.setItem("tempPasswords", JSON.stringify(stored));
  };

  const copyPassword = () => {
    navigator.clipboard.writeText(tempPassword);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const sharePassword = () => {
    navigator.clipboard.writeText(link);
    setShared(true);
    setTimeout(() => setShared(false), 2000);
  };

  const getStrength = () => {
    if (!tempPassword) return "";
    const hasUpper = /[A-Z]/.test(tempPassword);
    const hasLower = /[a-z]/.test(tempPassword);
    const hasNum = /[0-9]/.test(tempPassword);
    const hasSym = /[^a-zA-Z0-9]/.test(tempPassword);

    const types = [hasUpper, hasLower, hasNum, hasSym].filter(Boolean).length;

    if (tempPassword.length >= 12 && types >= 3) return "Strong 🔒";
    if (tempPassword.length >= 8 && types >= 2) return "Medium ⚠️";
    return "Weak ❌";
  };

  return (
<div className="p-6 mb-8 border rounded-2xl bg-gray-50 shadow-inner max-w-3xl mx-auto">
  {/* Header */}
  <h2 className="text-2xl md:text-3xl font-bold mb-4 text-gray-800 text-center">
    Temporary Password Generator
  </h2>
  <p className="text-gray-600 text-center mb-6 text-sm md:text-base">
    Generate strong temporary passwords with optional customization. You can set expiry, copy, download, or share them securely.
  </p>

  {/* Custom password input */}
  <div className="mb-4">
    <label className="block text-gray-700 font-medium mb-1">Custom Password (optional)</label>
    <input
      type="text"
      value={customPassword}
      onChange={(e) => setCustomPassword(e.target.value)}
      placeholder="Type your own password"
      className="w-full border p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 transition"
    />
  </div>

  {/* Password customization options */}
  {!customPassword && (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
      <div className="flex items-center gap-2">
        <label className="font-medium">Length:</label>
        <input
          type="number"
          min="4"
          max="32"
          value={length}
          onChange={(e) => setLength(Number(e.target.value))}
          className="border p-1 rounded-lg w-16 text-center"
        />
      </div>

      <div className="flex flex-wrap gap-4">
        <label className="flex items-center gap-1">
          <input
            type="checkbox"
            checked={useUpper}
            onChange={(e) => setUseUpper(e.target.checked)}
            className="accent-cyan-600"
          />
          Uppercase
        </label>
        <label className="flex items-center gap-1">
          <input
            type="checkbox"
            checked={useLower}
            onChange={(e) => setUseLower(e.target.checked)}
            className="accent-cyan-600"
          />
          Lowercase
        </label>
        <label className="flex items-center gap-1">
          <input
            type="checkbox"
            checked={useNumbers}
            onChange={(e) => setUseNumbers(e.target.checked)}
            className="accent-cyan-600"
          />
          Numbers
        </label>
        <label className="flex items-center gap-1">
          <input
            type="checkbox"
            checked={useSymbols}
            onChange={(e) => setUseSymbols(e.target.checked)}
            className="accent-cyan-600"
          />
          Symbols
        </label>
      </div>
    </div>
  )}

  {/* Expiry selection */}
  <div className="flex items-center gap-2 mb-6">
    <label className="font-medium">Expire in:</label>
    <select
      value={expireTime}
      onChange={(e) => setExpireTime(Number(e.target.value))}
      className="border p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 transition"
    >
      {[1, 5, 10, 30, 60].map((t) => (
        <option key={t} value={t}>
          {t} minute{t > 1 ? "s" : ""}
        </option>
      ))}
    </select>
  </div>

  {/* Generate button */}
  <button
    onClick={generateTemporaryPassword}
    className="w-full flex items-center justify-center gap-2 p-3 bg-cyan-600 text-white rounded-2xl shadow-lg hover:shadow-xl hover:scale-105 transition transform font-semibold mb-6"
  >
    <RefreshCw size={20} /> Generate Temporary Password
  </button>

  {/* Generated password display */}
  {tempPassword && (
    <div className="p-4 border rounded-lg bg-gray-100 mb-4 space-y-2">
      <div className="flex justify-between items-center">
        <span className="font-mono break-all text-gray-800 text-lg">
          {showPassword ? tempPassword : "••••••••••••"}
        </span>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowPassword((prev) => !prev)}
            className="text-gray-600 hover:text-gray-800"
            title={showPassword ? "Hide Password" : "Show Password"}
          >
            {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
          </button>
          <button
            onClick={copyPassword}
            className="text-gray-600 hover:text-gray-800"
            title="Copy Password"
          >
            <Copy size={20} />
          </button>
          {copied && <span className="text-green-600 text-sm ml-1 whitespace-nowrap">Copied!</span>}
        </div>
      </div>

      <p className="text-sm text-gray-700">Strength: {getStrength()}</p>

      {link && (
        <div className="flex justify-between items-center mt-2">
          <a href={link} className="text-blue-600 underline break-all">
            {link}
          </a>
          <div className="flex items-center gap-2">
            <button
              onClick={sharePassword}
              className="text-gray-600 hover:text-gray-800"
              title="Copy Link"
            >
              <Share2 size={20} />
            </button>
            {shared && (
              <span className="text-blue-600 text-sm ml-1 whitespace-nowrap">
                Link Copied!
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  )}
</div>

  );
}
